import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Loader2, ChevronDown } from "lucide-react";
import { toast } from "sonner";
import {
  resolveFundCbam,
  type FeBreakdownRow,
  type FeMappingRow,
  type FeSplitOverride,
} from "./feMatching";

const PORTFOLIOS = [1, 2, 3, 4, 5];

export default function FELookupEditor() {
  const qc = useQueryClient();

  const { data: mapping, isLoading } = useQuery({
    queryKey: ["fe-sector-mapping"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("fe_sector_mapping")
        .select("*")
        .order("display_order");
      if (error) throw error;
      return data as FeMappingRow[];
    },
  });

  const { data: masterSectors } = useQuery({
    queryKey: ["ba-master-sectors"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("ba_master_sectors")
        .select("id, name")
        .order("name");
      if (error) throw error;
      return data as { id: string; name: string }[];
    },
  });

  // Pull the same data that PSectorWeightings uses, so we can restrict the
  // dropdowns to only the CBAM sectors that actually appear on that page.
  const { data: weights } = useQuery({
    queryKey: ["ba-fund-weights-all"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("ba_cbam_fund_weights")
        .select("fund_id, portfolio, weight_pct, effective_date")
        .order("effective_date", { ascending: false });
      if (error) throw error;
      const seen = new Set<string>();
      const latest: { fund_id: string; portfolio: number; weight_pct: number }[] = [];
      for (const w of (data as any[])) {
        const key = `${w.fund_id}::${w.portfolio}`;
        if (seen.has(key)) continue;
        seen.add(key);
        latest.push(w);
      }
      return latest;
    },
  });

  const { data: mappings } = useQuery({
    queryKey: ["ba-fund-sector-mappings"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("ba_cbam_fund_sector_mappings")
        .select("fund_id, sector_id, weighting");
      if (error) throw error;
      return data as { fund_id: string; sector_id: string; weighting: number }[];
    },
  });

  const { data: cbamFunds } = useQuery({
    queryKey: ["ba-cbam-funds-isin"],
    queryFn: async () => {
      const { data, error } = await supabase.from("ba_cbam_funds").select("id, isin");
      if (error) throw error;
      return data as { id: string; isin: string | null }[];
    },
  });

  const { data: feRows } = useQuery({
    queryKey: ["fe-asset-breakdown-latest"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("fe_asset_breakdown")
        .select("id, isin, fund_name, as_of_date, breakdown")
        .order("as_of_date", { ascending: false });
      if (error) throw error;
      const latest = new Map<string, FeBreakdownRow>();
      for (const r of data ?? []) {
        const row = r as any;
        if (!latest.has(row.isin)) {
          latest.set(row.isin, { ...row, breakdown: row.breakdown ?? {} });
        }
      }
      return Array.from(latest.values());
    },
  });

  const { data: feOverrides } = useQuery({
    queryKey: ["fe-split-overrides"],
    queryFn: async () => {
      const { data, error } = await supabase.from("fe_fund_split_overrides").select("*");
      if (error) throw error;
      return data as FeSplitOverride[];
    },
  });

  // Compute the set of CBAM Master Sector NAMES that appear on
  // /partner/portfoliomanagement/p/p-sector-weightings (any P portfolio > 0).
  const eligibleSectorNames = (() => {
    if (!masterSectors || !weights || !mappings || !cbamFunds || !feRows || !mapping || !feOverrides) {
      return null; // loading
    }
    const heldFundIds = new Set(weights.filter((w) => Number(w.weight_pct) > 0).map((w) => w.fund_id));
    const sectorNameById = new Map(masterSectors.map((s) => [s.id, s.name]));
    const matrix = new Map<string, number>(); // `${p}-${name}` => %

    // Manual contributions
    for (const w of weights) {
      if (!heldFundIds.has(w.fund_id) || Number(w.weight_pct) <= 0) continue;
      for (const m of mappings.filter((m) => m.fund_id === w.fund_id)) {
        const name = sectorNameById.get(m.sector_id);
        if (!name) continue;
        const key = `${w.portfolio}-${name}`;
        matrix.set(key, (matrix.get(key) || 0) + Number(w.weight_pct) * Number(m.weighting));
      }
    }

    // FE-derived contributions
    const mappingByCol = new Map(mapping.map((m) => [m.fe_column_name, m]));
    const overridesByIsinCol = new Map<string, FeSplitOverride[]>();
    for (const o of feOverrides) {
      const k = `${o.isin}::${o.fe_column_name}`;
      if (!overridesByIsinCol.has(k)) overridesByIsinCol.set(k, []);
      overridesByIsinCol.get(k)!.push(o);
    }
    const isinByFundId = new Map(cbamFunds.map((f) => [f.id, (f.isin ?? "").toUpperCase().trim()]));
    const feByIsin = new Map<string, FeBreakdownRow>();
    for (const r of feRows) if (r.isin) feByIsin.set(r.isin.toUpperCase().trim(), r);

    for (const w of weights) {
      if (!heldFundIds.has(w.fund_id) || Number(w.weight_pct) <= 0) continue;
      const isin = isinByFundId.get(w.fund_id);
      if (!isin) continue;
      const fe = feByIsin.get(isin);
      if (!fe) continue;
      const { allocation, total } = resolveFundCbam(fe, mappingByCol, overridesByIsinCol);
      if (!total) continue;
      for (const [sectorName, pct] of Object.entries(allocation)) {
        if (sectorName.startsWith("[needs split] ") || sectorName === "Unknown") continue;
        const key = `${w.portfolio}-${sectorName}`;
        matrix.set(key, (matrix.get(key) || 0) + Number(w.weight_pct) * (Number(pct) / 100));
      }
    }

    const names = new Set<string>();
    for (const s of masterSectors) {
      if (PORTFOLIOS.some((p) => (matrix.get(`${p}-${s.name}`) || 0) > 0)) {
        names.add(s.name);
      }
    }
    return names;
  })();

  const updateMut = useMutation({
    mutationFn: async (row: Partial<FeMappingRow> & { fe_column_name: string }) => {
      const { error } = await supabase
        .from("fe_sector_mapping")
        .update({
          cbam_master_sector: row.cbam_master_sector,
          mapping_type: row.mapping_type,
          children: row.children,
        })
        .eq("fe_column_name", row.fe_column_name);
      if (error) throw error;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["fe-sector-mapping"] });
      toast.success("Lookup updated");
    },
    onError: (e: Error) => toast.error(e.message),
  });

  // Selectable sectors: filter master sectors to those present on P page.
  // While eligibility is still loading, fall back to the full list to avoid
  // hiding everything.
  const selectableSectors = (masterSectors ?? []).filter((s) =>
    eligibleSectorNames ? eligibleSectorNames.has(s.name) : true,
  );

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">FE → CBAM Lookup Conversion</CardTitle>
        <CardDescription>
          Each FE export column maps to a CBAM Master Sector. SPLIT and EXPANDED parents are
          allocated across child sectors per-fund on the Fund Sector Weighting tab. Only CBAM
          sectors currently present on the P Sector Weightings page are selectable.
        </CardDescription>
      </CardHeader>
      <CardContent className="p-0 overflow-x-auto">
        {isLoading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
          </div>
        ) : (
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b bg-muted/50">
                <th className="text-left p-3 font-medium text-muted-foreground min-w-[260px]">
                  FE Column
                </th>
                <th className="text-left p-3 font-medium text-muted-foreground min-w-[140px]">
                  Type
                </th>
                <th className="text-left p-3 font-medium text-muted-foreground min-w-[260px]">
                  CBAM Master Sector (DIRECT)
                </th>
                <th className="text-left p-3 font-medium text-muted-foreground">
                  Children (SPLIT / EXPANDED)
                </th>
              </tr>
            </thead>
            <tbody>
              {mapping?.map((row, idx) => (
                <tr
                  key={row.fe_column_name}
                  className={idx % 2 === 0 ? "border-b bg-background" : "border-b bg-muted/20"}
                >
                  <td className="p-3 text-xs font-medium">{row.fe_column_name}</td>
                  <td className="p-2">
                    <Select
                      value={row.mapping_type}
                      onValueChange={(v) =>
                        updateMut.mutate({
                          fe_column_name: row.fe_column_name,
                          mapping_type: v as FeMappingRow["mapping_type"],
                          cbam_master_sector: row.cbam_master_sector,
                          children: row.children,
                        })
                      }
                    >
                      <SelectTrigger className="h-8 text-xs">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="DIRECT">DIRECT</SelectItem>
                        <SelectItem value="SPLIT">SPLIT</SelectItem>
                        <SelectItem value="EXPANDED">EXPANDED</SelectItem>
                      </SelectContent>
                    </Select>
                  </td>
                  <td className="p-2">
                    <Select
                      value={row.cbam_master_sector ?? ""}
                      disabled={row.mapping_type !== "DIRECT"}
                      onValueChange={(v) => {
                        const next = v === "__none__" ? null : v;
                        if (next !== row.cbam_master_sector) {
                          updateMut.mutate({
                            fe_column_name: row.fe_column_name,
                            mapping_type: row.mapping_type,
                            cbam_master_sector: next,
                            children: row.children,
                          });
                        }
                      }}
                    >
                      <SelectTrigger className="h-8 text-xs">
                        <SelectValue placeholder={row.mapping_type === "DIRECT" ? "Select sector…" : "—"} />
                      </SelectTrigger>
                      <SelectContent className="max-h-72">
                        <SelectItem value="__none__">— None —</SelectItem>
                        {selectableSectors.map((s) => (
                          <SelectItem key={s.id} value={s.name}>{s.name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </td>
                  <td className="p-2">
                    {row.mapping_type === "DIRECT" ? (
                      <span className="text-xs text-muted-foreground">—</span>
                    ) : (
                      <Popover>
                        <PopoverTrigger asChild>
                          <Button
                            variant="outline"
                            size="sm"
                            className="h-8 w-full justify-between text-xs font-normal"
                          >
                            <span className="truncate">
                              {(row.children ?? []).length > 0
                                ? `${(row.children ?? []).length} selected`
                                : "Select child sectors…"}
                            </span>
                            <ChevronDown className="h-3 w-3 opacity-50 shrink-0 ml-2" />
                          </Button>
                        </PopoverTrigger>
                        <PopoverContent className="w-72 p-0" align="start">
                          <div className="max-h-72 overflow-y-auto p-1">
                            {selectableSectors.map((s) => {
                              const selected = (row.children ?? []).includes(s.name);
                              return (
                                <label
                                  key={s.id}
                                  className="flex items-center gap-2 px-2 py-1.5 text-xs rounded hover:bg-accent cursor-pointer"
                                >
                                  <Checkbox
                                    checked={selected}
                                    onCheckedChange={(checked) => {
                                      const current = row.children ?? [];
                                      const next = checked
                                        ? [...current, s.name]
                                        : current.filter((c) => c !== s.name);
                                      updateMut.mutate({
                                        fe_column_name: row.fe_column_name,
                                        mapping_type: row.mapping_type,
                                        cbam_master_sector: row.cbam_master_sector,
                                        children: next,
                                      });
                                    }}
                                  />
                                  <span>{s.name}</span>
                                </label>
                              );
                            })}
                          </div>
                        </PopoverContent>
                      </Popover>
                    )}
                    {row.mapping_type !== "DIRECT" && (row.children ?? []).length > 0 && (
                      <div className="mt-1 flex flex-wrap gap-1">
                        {(row.children ?? []).map((c) => (
                          <Badge key={c} variant="outline" className="text-[10px]">
                            {c}
                          </Badge>
                        ))}
                      </div>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </CardContent>
    </Card>
  );
}
